import {
  FLEET_AREA_GROUP_LABELS,
  FLEET_AREA_LABELS,
  FleetAreaGroup,
  type FleetAreaCatalogItem,
  type FleetAreaGroup as AreaGroup,
} from '@gb-ticket/shared-types';
import { toast } from 'sonner';

import { Switch } from '@/components/ui/switch';
import { ApiRequestError } from '@/lib/api';
import { fleetPanelClassName } from '@/products/fleet/features/fleet-panel';
import { useFleetAreas, usePatchFleetArea } from '@/products/fleet/features/use-fleet-areas';

const GROUP_ORDER: AreaGroup[] = [
  FleetAreaGroup.DAILY,
  FleetAreaGroup.DUTIES,
  FleetAreaGroup.OPERATIONS,
];

export function FleetAreasPanel() {
  const areas = useFleetAreas();
  const patch = usePatchFleetArea();
  const items = areas.data?.items ?? [];

  async function apply(code: FleetAreaCatalogItem['code'], enabled: boolean) {
    try {
      await patch.mutateAsync({ code, enabled });
      toast.success(
        enabled
          ? `${FLEET_AREA_LABELS[code]} ist eingeschaltet.`
          : `${FLEET_AREA_LABELS[code]} ist ausgeschaltet.`,
      );
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Bereich konnte nicht geändert werden.',
      );
    }
  }

  return (
    <section className="space-y-4" aria-labelledby="fleet-areas-heading">
      <div>
        <h2 id="fleet-areas-heading" className="text-base font-semibold">
          Bereiche
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Ausgeschaltete Bereiche nehmen keine neuen Einträge an. Vorhandene Daten bleiben lesbar.
        </p>
      </div>
      {areas.isPending ? (
        <p className="text-sm text-muted-foreground">Bereiche werden geladen …</p>
      ) : null}
      {areas.isError ? (
        <p className="text-sm text-destructive">
          {areas.error instanceof ApiRequestError
            ? areas.error.message
            : 'Die Bereiche konnten nicht geladen werden.'}
        </p>
      ) : null}
      {GROUP_ORDER.map((group) => {
        const groupItems = items.filter((item) => item.group === group);
        if (groupItems.length === 0) {
          return null;
        }
        return (
          <div key={group} className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">
              {FLEET_AREA_GROUP_LABELS[group]}
            </h3>
            <ul className="space-y-2">
              {groupItems.map((item) => {
                const titleId = `fleet-area-${item.code}`;
                const busy = patch.isPending && patch.variables?.code === item.code;
                return (
                  <li
                    key={item.code}
                    className={`flex items-start justify-between gap-4 ${fleetPanelClassName}`}
                  >
                    <div>
                      <p id={titleId} className="font-medium">
                        {item.label}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{item.hides.join(' · ')}</p>
                    </div>
                    <Switch
                      checked={item.enabled}
                      disabled={busy}
                      aria-labelledby={titleId}
                      onCheckedChange={(checked) => {
                        void apply(item.code, checked);
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </section>
  );
}
